import React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import { observable, action, autorun } from 'mobx';
import emojione from 'lib/emojione/emojione';
import { replace } from 'lib/emojione/ascii-mapping';
import { withRouter } from 'react-router';

import FormView from 'lib/form/form-view';
import computed from 'lib/mobx/computed';
import { Form, Field, OrValidator, OtherFieldValidator } from 'lib/form/form';
import withDialog from 'lib/alerts/with-dialog';
import { ValueFieldData, RequiredValidator, MinMaxLengthValidator } from 'lib/form/value-field-data';
import { ArrayOfFieldData, NotEmptyValidator } from 'lib/form/array-of-field-data';
import Styled from 'styled';
import { RetryDialog } from 'lib/alerts/dialog';

import withPhotofiles from 'four-nets/photofile/form/with-photofiles';
import withStackViewForm from 'four-nets/ui/with-stack-view-form';
import withUsers from 'four-nets/user/form/with-users';

import FieldWithLabel from 'four-nets/ui-v2/form/field-with-label/field-with-label';
import TextArea from 'four-nets/ui-v2/form/text-area/text-area';
import Select from 'four-nets/ui-v2/form/select/select';
import Input from 'four-nets/ui-v2/form/input/input';
import { PhotoFileStore } from 'four-nets/photofile/photofile';
import UsersInputView from 'four-nets/user/form/users-input/users-input-view';
import PhotoFileUploadView from 'four-nets/photofile/form/photofile-upload-view';

import defaultStyles from './new-thread-view.scss';

@withPhotofiles({ fieldName: 'images' })
class NewThreadForm extends withUsers({ fieldName: 'participants' })(Form) {
    constructor(options) {
        const { title = '', body = '', isPrivate = false, participants = [] } = options;

        super({
            ...options,
            field: {
                isPrivate: new Field({
                    data: new ValueFieldData({
                        value: isPrivate,
                        validators: [new RequiredValidator()],
                    }),
                }),
                category: new Field({
                    data: new ValueFieldData({
                        value: null,
                        validators: [new RequiredValidator()],
                    }),
                }),
                participants: new Field({
                    data: new ArrayOfFieldData({
                        fields: participants.map(user => new ValueFieldData({ value: user })),
                    }),
                }),
                username: new Field({
                    data: new ValueFieldData({
                        value: '',
                        validators: [
                            new OrValidator({
                                validators: [
                                    new RequiredValidator(),
                                    new OtherFieldValidator({
                                        fieldName: 'participants',
                                        validator: new NotEmptyValidator(),
                                    }),
                                ],
                            }),
                        ],
                    }),
                }),
                title: new Field({
                    data: new ValueFieldData({
                        value: title,
                        validators: [new RequiredValidator(), new MinMaxLengthValidator({ max: 100 })],
                    }),
                }),
                body: new Field({
                    data: new ValueFieldData({
                        value: body,
                        validators: [new RequiredValidator()],
                    }),
                }),
                images: new Field({
                    data: new ArrayOfFieldData(),
                }),
            },
        });
    }

    computeValid() {
        const isPrivate = this.field.get('isPrivate').data.value;
        const entries = this.field.entries();

        for (let i = 0; i < entries.length; i++) {
            const [name, { data }] = entries[i];

            if (!((name === 'category' && isPrivate) || (!isPrivate && name === 'username')) && !data.valid) {
                return false;
            }
        }

        if (isPrivate) {
            return this.usersValid();
        }

        return true;
    }
}

export class NewThreadViewModel {
    categoryStore;
    photoFileStore;
    hidePrivateDiscussions;

    @observable form;
    @observable loading = false;
    @observable superCategory;
    @observable loading;

    @observable selectedSuperCategory = 'default';
    @observable selectedCategory = 'default';

    disposer;

    constructor(options) {
        const { categoryStore } = options;
        this.categoryStore = categoryStore;

        this.hidePrivateDiscussions = options.hidePrivateDiscussions;

        if (this.hidePrivateDiscussions === undefined) {
            this.hidePrivateDiscussions = true;
        }

        this.photoFileStore = new PhotoFileStore({
            api: categoryStore.api.api,
        });
    }

    handleSubmit = ({ data }) => {
        const { participants, category, title, body, images } = data;

        return this.categoryStore.threadStore.create({
            users: participants,
            category,
            title,
            body: emojione.toShort(replace(body)),
            images,
        });
    };

    @action setupForm = (options = {}) => {
        const { title, body, participants, isPrivate, category } = options;

        const form = new NewThreadForm({
            photoFileStore: this.photoFileStore,
            title,
            body,
            participants,
            isPrivate,
            handleSubmit: this.handleSubmit,
        });

        this.form = form;
        if (category) {
            this.setSuperCategory(category.superCategoryName);
            this.selectedSuperCategory = category.superCategoryName;
            this.selectedCategory = category.id;
            this.changeCategory(category.id);
        }
        return form;
    };

    @computed get superCategories() {
        const { superCategories } = this.categoryStore;

        if (this.hidePrivateDiscussions) {
            return superCategories.filter(sc => sc.row > 0);
        }

        return superCategories;
    }

    @computed get categories() {
        if (this.superCategory) {
            return this.superCategory.categories;
        }
        return [];
    }

    @action setSuperCategory(name) {
        const { superCategories } = this.categoryStore;

        for (let i = 0; i < superCategories.length; i++) {
            if (name === superCategories[i].name) {
                this.superCategory = superCategories[i];
            }
        }
        this.selectedCategory = 'default';
        this.form.field.get('category').data.setValue({ value: null });
    }

    changeCategory(categoryId) {
        this.form.field.get('category').data.setValue({ value: this.categoryStore.data.get(categoryId) });
    }

    loadCategories() {
        if (!this.categoryStore.didLoadAll) {
            this.loading = true;
            return this.categoryStore.loadAll().finally(() => {
                this.loading = false;
            });
        }
        return Promise.resolve();
    }
}

@withStackViewForm({ title: <FormattedMessage id='NEW_THREAD_FORM_TITLE' />, promptOnClose: true })
@withDialog
@Styled(defaultStyles)
@observer
class NewThreadView extends React.Component {
    bodyTextArea;

    setBodyTextArea = ref => {
        this.bodyTextArea = ref;
    };

    setSuperCategory = event => {
        this.props.newThreadViewModel.setSuperCategory(event.target.value);
    };

    setCategory = event => {
        this.props.newThreadViewModel.changeCategory(parseInt(event.target.value, 10));
    };

    submit = () => {
        this.props.submit().then(thread => {
            this.props.history.push(thread.url);
        });
    };

    componentDidMount() {
        this.disposer = autorun(() => {
            this.props.setLoading(this.props.newThreadViewModel.loading);
        });
        this.props.newThreadViewModel.loadCategories().catch(err => {
            this.props.showDialog(
                new RetryDialog({
                    message: err.message,
                    retry: this.props.newThreadViewModel.loadCategories.bind(this.props.newThreadViewModel),
                })
            );
        });
    }

    componentWillUnmount() {
        this.disposer();
    }

    render() {
        const { newThreadViewModel, form } = this.props;
        const { usersSearch, users, valid, dirty } = form;
        const { loading } = usersSearch;

        const { styles } = this.props;
        const isPrivate = form.field.get('isPrivate').data.value;

        return (
            <FormView
                style={{ form: styles.form }}
                form={form}
                submitOnEnter={false}
                submit={this.submit}
                className={classnames(styles.form, {
                    [styles.form__private]: isPrivate,
                })}
            >
                <div className={styles.inputs}>
                    <div className={styles.superCategory} key='superCategory'>
                        <FieldWithLabel
                            key='supercategory'
                            field={
                                <Select
                                    style={{ container: styles.inputColors }}
                                    onChange={this.setSuperCategory}
                                    defaultValue={newThreadViewModel.selectedSuperCategory}
                                >
                                    <FormattedMessage id='BAZAAR_CHOOSE'>
                                        {msg => (
                                            <option disabled value='default'>
                                                {msg}
                                            </option>
                                        )}
                                    </FormattedMessage>
                                    {newThreadViewModel.superCategories
                                        .filter(
                                            superCategory => ['Dni s...', 'Dny s...'].indexOf(superCategory.name) === -1
                                        )
                                        .map(superCategory => (
                                            <option value={superCategory.name} key={superCategory.name}>
                                                {superCategory.name}
                                            </option>
                                        ))}
                                </Select>
                            }
                            label={<FormattedMessage id='FORUM_THREAD_SUPER_CATEGORY' />}
                            id='new-thread-super-category'
                        />
                    </div>
                    <div className={styles.category} key='category'>
                        <FieldWithLabel
                            field={
                                <Select
                                    style={{ container: styles.inputColors }}
                                    onChange={this.setCategory}
                                    key={
                                        (newThreadViewModel.superCategory && newThreadViewModel.superCategory.name) ||
                                        'default'
                                    }
                                    field={this.props.form.field.get('category')}
                                    defaultValue={newThreadViewModel.selectedCategory}
                                >
                                    <FormattedMessage id='BAZAAR_CHOOSE'>
                                        {msg => (
                                            <option disabled value='default'>
                                                {msg}
                                            </option>
                                        )}
                                    </FormattedMessage>
                                    {newThreadViewModel.superCategory &&
                                        // #FIXME this filtering should be in model really.
                                        // Categories probably should have flags if
                                        // they are "writeable" to users
                                        newThreadViewModel.superCategory.categories
                                            .filter(
                                                c => ['mesiac-s', 'mesic-s', 'soutez', 'sutaz'].indexOf(c.slug) === -1
                                            )
                                            .map(category => (
                                                <option value={category.id} key={category.id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                </Select>
                            }
                            label={<FormattedMessage id='FORUM_THREAD_CATEGORY' />}
                            id='new-thread-category'
                        />
                    </div>
                    <div className={styles.username} key='username'>
                        <FieldWithLabel
                            field={
                                <UsersInputView
                                    field={form.field.get('participants')}
                                    searchField={form.field.get('username')}
                                    loading={loading}
                                    autocompleteItems={users}
                                    dynamicWidth={true}
                                />
                            }
                            label={<FormattedMessage id='FORUM_MEMBERS_LIST' />}
                            id='new-thread-participants'
                        />
                    </div>
                    <div className={styles.title} key='title'>
                        <FieldWithLabel
                            key='title'
                            field={
                                <Input
                                    style={{ textInput: styles.inputColors }}
                                    {...form.field.get('title').data}
                                    type='text'
                                    id='id_title'
                                />
                            }
                            label={<FormattedMessage id='THREAD_NAME' />}
                            id='new-thread-title'
                        />
                    </div>
                    <div className={styles.content} key='content'>
                        <FieldWithLabel
                            key='content'
                            field={<TextArea style={{ textArea: styles.inputColors }} field={form.field.get('body')} />}
                            label={<FormattedMessage id='FIRST_POST' />}
                            id='new-thread-body'
                        />
                    </div>
                    <div className={styles.images} key='images'>
                        <FieldWithLabel
                            key='images'
                            field={
                                <PhotoFileUploadView
                                    style={{ input: styles.input }}
                                    field={form.field.get('images')}
                                    fieldDataForFiles={form.photoFileFieldDataForFiles}
                                />
                            }
                            label={<FormattedMessage id='PRODUCT_IMAGES' />}
                            id='new-thread-images'
                        />
                    </div>

                    <div className={styles.footer}>
                        <button
                            type='button'
                            className={styles.submitButton}
                            disabled={!(valid && dirty)}
                            onClick={this.submit}
                        >
                            <FormattedMessage id='THREAD_SAVE' />
                        </button>
                    </div>
                </div>
            </FormView>
        );
    }
}

export default withRouter(NewThreadView);
